import { useCallback, useEffect, useState } from "react";
import { parseCustomDomainInput, type CustomDomainStatus } from "@farmacias/schema";
import {
  getDomain,
  painelApiConfigured,
  removeDomain,
  setDomain,
  type DomainInfo,
} from "../painel-api";

const BASE_DOMAIN = "sites.oniochat.com.br";

const STATUS_LABEL: Record<CustomDomainStatus, string> = {
  none: "Sem domínio próprio",
  pending_dns: "⏳ Aguardando DNS",
  provisioning: "🔐 Emitindo certificado",
  active: "🟢 Ativo",
  error: "🔴 Erro",
};

const STATUS_CLASS: Record<CustomDomainStatus, string> = {
  none: "bg-slate-100 text-slate-600",
  pending_dns: "bg-amber-100 text-amber-800",
  provisioning: "bg-blue-100 text-blue-800",
  active: "bg-green-100 text-green-800",
  error: "bg-red-100 text-red-700",
};

/**
 * O que se digita no campo "Nome"/"Host" do painel de DNS. O par provisionado é
 * sempre apex + www, então o apex vira `@` — a convenção da maioria dos
 * provedores para o domínio raiz.
 */
function nomeNoDns(host: string): string {
  return host.startsWith("www.") ? "www" : "@";
}

function Copy({ value }: { value: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="btn-ghost !px-2 !py-0.5 text-xs"
      onClick={() => {
        void navigator.clipboard?.writeText(value);
        setDone(true);
        setTimeout(() => setDone(false), 1200);
      }}
    >
      {done ? "copiado" : "copiar"}
    </button>
  );
}

export function CustomDomainCard({ slug }: { slug: string }) {
  const [info, setInfo] = useState<DomainInfo | null>(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      setInfo(await getDomain(slug));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erro ao carregar domínio");
    }
  }, [slug]);

  useEffect(() => {
    if (painelApiConfigured) void load();
  }, [load]);

  if (!painelApiConfigured) {
    return (
      <div className="card space-y-1">
        <h2 className="text-sm font-semibold">Domínio próprio</h2>
        <p className="text-xs text-slate-500">
          Indisponível neste ambiente (a painel-api não está configurada).
        </p>
      </div>
    );
  }

  const preview = parseCustomDomainInput(input, BASE_DOMAIN);

  async function run(fn: () => Promise<DomainInfo>, ok: string) {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      setInfo(await fn());
      setMsg(ok);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erro");
    } finally {
      setBusy(false);
    }
  }

  const hasDomain = !!info?.domain;

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Domínio próprio</h2>
        {info && (
          <span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[info.status]}`}>
            {STATUS_LABEL[info.status]}
          </span>
        )}
      </div>

      {!hasDomain && (
        <div className="space-y-2">
          <input
            className="input"
            placeholder="www.drogariax.com.br"
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          {input && !preview.ok && <p className="text-xs text-red-600">{preview.error}</p>}
          {preview.ok && (
            <p className="text-xs text-slate-500">
              Cobre <code>{preview.primary}</code> e <code>{preview.secondary}</code>.
            </p>
          )}
          <button
            className="btn-primary w-full"
            disabled={busy || !preview.ok}
            onClick={() => preview.ok && void run(() => setDomain(slug, preview.primary), "Domínio adicionado. Passe o registro A abaixo para a farmácia.")}
          >
            Adicionar domínio
          </button>
        </div>
      )}

      {hasDomain && info && (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            A farmácia precisa criar estes <strong>dois</strong> registros no painel de DNS do
            domínio dela. Os dois são do mesmo tipo e apontam para o mesmo IP:
          </p>
          <div className="space-y-2">
            {info.hosts.map((h) => (
              <div key={h.host} className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                <div className="mb-2 flex items-center justify-between gap-2 border-b border-slate-200 pb-2">
                  <code className="break-all text-xs font-medium text-slate-700">{h.host}</code>
                  <span className="flex shrink-0 items-center gap-1">
                    {/* O status por host só aparece quando difere do geral — repetir
                        o mesmo rótulo três vezes no card só polui. */}
                    {h.status !== info.status && (
                      <span className={`rounded px-1.5 py-0.5 text-[11px] ${STATUS_CLASS[h.status]}`}>
                        {STATUS_LABEL[h.status]}
                      </span>
                    )}
                    <Copy value={h.host} />
                  </span>
                </div>
                <dl className="grid grid-cols-[3.2rem_1fr_auto] items-center gap-x-2 gap-y-1.5 text-xs">
                  <dt className="text-slate-400">Tipo</dt>
                  <dd className="font-mono font-semibold text-slate-700">A</dd>
                  <dd />
                  <dt className="text-slate-400">Nome</dt>
                  <dd className="font-mono text-slate-700">{nomeNoDns(h.host)}</dd>
                  <dd />
                  <dt className="text-slate-400">Valor</dt>
                  <dd className="font-mono text-slate-700">{h.aRecord}</dd>
                  <dd>
                    <Copy value={h.aRecord} />
                  </dd>
                </dl>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400">
            Alguns provedores pedem o domínio completo no campo <em>Nome</em> em vez de{" "}
            <code>@</code>. Se já existir um registro A ou CNAME nesses nomes, ele precisa ser
            removido.
          </p>
          {info.hosts.find((h) => h.detail)?.detail && (
            <p className="text-xs text-slate-500">{info.hosts.find((h) => h.detail)?.detail}</p>
          )}
          {info.status === "active" && (
            <a
              className="block text-xs text-brand-dark underline"
              href={`https://${info.domain}`}
              target="_blank"
              rel="noreferrer"
            >
              {info.domain}
            </a>
          )}
          <div className="flex gap-2">
            <button className="btn-ghost flex-1" disabled={busy} onClick={() => void run(() => getDomain(slug), "Status atualizado.")}>
              Verificar agora
            </button>
            <button
              className="btn-danger flex-1"
              disabled={busy}
              onClick={() => {
                if (confirm(`Remover o domínio ${info.domain}? O certificado será revogado.`)) {
                  void run(() => removeDomain(slug), "Domínio removido.");
                }
              }}
            >
              Remover
            </button>
          </div>
        </div>
      )}

      {msg && <p className="text-xs text-green-700">{msg}</p>}
      {err && <p className="text-xs text-red-600">{err}</p>}
    </div>
  );
}
